import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Button } from '../components';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Users, Building2, CheckCircle, XCircle, FileSignature, UserPlus } from 'lucide-react';
import { ReplacementModal } from '../components/ReplacementModal';
import { useTranslation } from 'react-i18next';

export const ProfessionalConnectionsPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replacementModal, setReplacementModal] = useState<{
    connectionId: number;
    employerName: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['professional-connections'],
    queryFn: async () => {
      const res = await api.get('/professional/connections');
      return res.data.data || [];
    }
  });

  const acceptMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      const res = await api.post(`/professional/connections/${connectionId}/accept`);
      return res.data;
    },
    onSuccess: (_data, connectionId) => {
      toast.success(t('connections.acceptedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['professional-connections'] });
      navigate(`/professional/connections/${connectionId}/agreements`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('connections.acceptError'));
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      const res = await api.post(`/professional/connections/${connectionId}/reject`);
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('connections.rejectedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['professional-connections'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('connections.rejectError'));
    }
  });

  const connections = data || [];

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-12">
            <p className="text-red-600">{t('connections.loadError')}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('connections.title')}</h1>
        <p className="mt-1 text-gray-600">
          {t('connections.subtitle')}
        </p>
      </div>

      {connections.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('connections.empty')}</h2>
            <p className="text-gray-600 mb-4">{t('connections.emptyDesc')}</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {connections.map((c: any) => (
            <Card key={c.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {c.employer_company_name || `${c.employer_first_name || ''} ${c.employer_last_name || ''}`.trim() || t('connections.defaultEmployerName')}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">{c.employer_email}</p>
                  </div>
                </div>

                <div className="flex items-center mb-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    c.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : c.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                  }`}>
                    {c.status === 'active' ? t('connections.statusActive')
                      : c.status === 'pending' ? t('connections.statusPending')
                        : c.status}
                  </span>
                </div>

                {c.status === 'pending' ? (
                  <div className="flex gap-2">
                    <Button
                      className="w-full"
                      loading={acceptMutation.isPending}
                      onClick={() => acceptMutation.mutate(c.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('connections.accept')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      loading={rejectMutation.isPending}
                      onClick={() => rejectMutation.mutate(c.id)}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      {t('connections.reject')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link to={`/professional/connections/${c.id}/agreements`} className="block">
                      <Button className="w-full">
                        <FileSignature className="w-4 h-4 mr-2" />
                        {c.agreement_id ? t('connections.viewAgreements') : t('connections.makeAgreements')}
                      </Button>
                    </Link>
                    {c.status === 'active' && (
                      <button
                        type="button"
                        onClick={() => setReplacementModal({
                          connectionId: c.id,
                          employerName: c.employer_company_name || `${c.employer_first_name || ''} ${c.employer_last_name || ''}`.trim() || t('connections.defaultEmployerName')
                        })}
                        className="w-full flex items-center justify-center gap-1.5 py-2 px-3 border border-gray-200 rounded-md text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                      >
                        <UserPlus className="w-4 h-4" />
                        {t('connections.registerReplacement')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      {replacementModal && (
        <ReplacementModal
          connectionId={replacementModal.connectionId}
          professionalName={replacementModal.employerName}
          mode="professional"
          onClose={() => setReplacementModal(null)}
        />
      )}
    </div>
  );
};
